import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AspApi from 'api/ApiConfig';

const initialState = {
  toggleToast: [],
  currentBrand: null,
  currentAccount: null,
  globalLoader: false,
  featureFlags: {
    ams_feature: true,
    api_trigger_feature: true,
    webchat_feature: false,
    keyword_trigger_feature: true,
    rag_whatsapp_chat_system: false,
    whatsapp_flow_feature: false,
    whatsapp_payment_feature: false,
  },

  isSocketConnected: false,

  // sidebar
  expand: true,

  // on-boarding
  acc_id: null,
  brand_id: null,

  // current user
  currentUser: null,
  currentUserLoading: false,
  currentUserError: null,

  brandUserRoles: [],
  isAccountOwner: false,

  accountUpdate: false,
};

const fetchCurrentUser = createAsyncThunk(
  'app/fetchCurrentUser',
  async ({ rejectWithValue }) => {
    try {
      const res = await AspApi.get(
        'api/v1/svc/aaa/authentication/current_user',
      );

      return res?.data?.data;
    } catch (error) {
      return rejectWithValue(error || 'Failed to Fetch Current User');
    }
  },
);

export const authSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    updateToggleToast: (state, action) => {
      state.toggleToast = action.payload;
    },
    updateCurrentUser: (state, action) => {
      const account_details = {
        ...action.payload,
        account_created:
          action?.payload?.active_accounts?.length > 0 ? true : false,
        brand_created: false,
      };
      state.currentUser = action.payload;
    },
    updateAccountDetails: (state, action) => {
      state.currentAccount = action?.payload?.currentAccount;
      state.currentBrand = action?.payload?.currentBrand;

      // Extract feature flags from currentBrand
      if (action?.payload?.currentBrand?.feature_flag) {
        state.featureFlags = action?.payload?.currentBrand?.feature_flag;
      }
    },
    updateCurrentBrand: (state, action) => {
      state.currentBrand = action.payload;

      // Extract feature flags from brand
      if (action?.payload?.feature_flag) {
        state.featureFlags = action?.payload?.feature_flag;
      }
    },
    reset_feature_flag: (state, action) => {
      state.featureFlags = {
        ams_feature: true,
        api_trigger_feature: true,
        webchat_feature: false,
        keyword_trigger_feature: true,
        rag_whatsapp_chat_system: false,
        whatsapp_payment_feature: false,
        whatsapp_flow_feature: false,
      };
    },
    updateCurrentAccount: (state, action) => {
      state.currentAccount = action.payload;
    },
    updateGlobalLoader: (state, action) => {
      state.globalLoader = action.payload;
    },
    setBrandUserRoles: (state, action) => {
      state.isAccountOwner = state.currentAccount?.role === 'Account Owner';
      if (
        state?.acc_id &&
        state?.brand_id &&
        action.payload?.active_accounts?.length > 0
      ) {
        const current_account = action.payload?.active_accounts?.find(
          (account) => account?.account_id === state?.acc_id,
        );

        if (!current_account) {
          console.log('cant locate current account');
          return;
        }
        const current_brand = current_account?.brands;

        if (!current_brand?.length > 0) {
          console.log('User has no brands');
          return;
        }

        const brand_roles = current_brand?.filter(
          (brand) => brand?.brand_id === state?.brand_id,
        );

        if (!brand_roles?.length > 0) {
          console.log('User does not have roles');
          return;
        }

        state.brandUserRoles = brand_roles;
      }
    },
    clear: (state, action) => {
      state.toggleToast = action?.payload?.toggleToast;
      state.currentUser = action?.payload?.currentUser;
      state.currentBrand = action?.payload?.currentBrand;
      state.currentAccount = action?.payload?.currentAccount;
      state.globalLoader = action?.payload?.globalLoader;
    },

    UpdateOnboardingAccountId: (state, action) => {
      const { acc_id } = action.payload;
      state.acc_id = acc_id;
    },

    UpdateOnboardingBrandId: (state, action) => {
      const { brand_id } = action.payload;
      state.brand_id = brand_id;
    },

    UpdateAccountAndBrandId: (state, action) => {
      const { acc_id, brand_id } = action.payload;
      state.acc_id = acc_id;
      state.brand_id = brand_id;
    },
    setUpdateAccount: (state, action) => {
      state.accountUpdate = action.payload;
    },
    setExpand: (state, action) => {
      state.expand = action.payload;
    },
    updateCurrentBrandStatus: (state, action) => {
      state.currentBrand = {
        ...state.currentBrand,
        status: action.payload,
        is_whatsapp_connected: false,
      };
    },
    setSocketConnection: (state, action) => {
      state.isSocketConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.currentUserLoading = true;
        state.currentUserError = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = null;
        state.currentUserError = action.error;
      });
  },
});

export const {
  updateToggleToast,
  updateCurrentUser,
  updateCurrentBrand,
  updateAccountDetails,
  updateGlobalLoader,
  updateCurrentAccount,
  clear,
  UpdateOnboardingAccountId,
  UpdateOnboardingBrandId,
  UpdateAccountAndBrandId,
  setUpdateAccount,
  setExpand,
  updateCurrentBrandStatus,
  setBrandUserRoles,
  setSocketConnection,
  reset_feature_flag,
} = authSlice.actions;

export default authSlice.reducer;

export { fetchCurrentUser };
